import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "../../utils/cn";
import { AnimatePresence, motion } from "framer-motion";
import { Role } from "@/types/roles";
import { tenantAdminItems } from "./sidebarItems";
import { useAuth } from "@/hooks/useAuth";

export interface SidebarItem {
  id: string;
  label: string;
  icon: any;
  path?: string;
  badge?: string | number;
  disabled?: boolean;
  children?: Omit<SidebarItem, "children">[];
}

export interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
  onClose?: () => void;
  isMobile?: boolean;
  className?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  collapsed = false,
  onToggle,
  onClose,
  isMobile = false,
  className,
}) => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(
    new Set(["dashboard"]),
  );
  const [sideBarItems, setSideBarItems] = useState<SidebarItem[]>([]);
  const [pendingScrollToBottom, setPendingScrollToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  const pruneForRole = (
    items: SidebarItem[],
    role?: Role,
    canManageAdmin = true,
    canManageAgent = true,
    canManageTransaction = true,
    canManageSponsor = true,
    canManageRefund = true,
  ): SidebarItem[] => {
    if (!role) return items;

    if (role === Role.CALLING_AGENT) {
      const allowed = new Set([
        "agent-campaigns",
        "tasks",
        "auto-dialer",
        "task-calendar",
        "all-appointments",
        "settings",
        "profile",
      ]);

      const deepWhitelist = (list: SidebarItem[]): SidebarItem[] =>
        list
          .map((item) => {
            const hasChildren =
              Array.isArray(item.children) && item.children.length > 0;
            if (allowed.has(item.id)) {
              const children = hasChildren
                ? deepWhitelist(item.children!)
                : undefined;
              return { ...item, children };
            }
            if (hasChildren) {
              const children = deepWhitelist(item.children!);
              if (children.length > 0) return { ...item, children };
            }
            return null;
          })
          .filter(Boolean) as SidebarItem[];

      return deepWhitelist(items);
    }

    const blockedIds = new Set<string>();

    switch (role) {
      case Role.SALES_AGENT:
        if (!canManageSponsor) blockedIds.add("sponsor");

        blockedIds.add("roles-and-permission");
        blockedIds.add("ai-agent");
        blockedIds.add("users");
        blockedIds.add("embedded-forms");
        blockedIds.add("admin/dashboard");

        break;

      case Role.ADMIN:
        if (!canManageAdmin) blockedIds.add("users");
        if (!canManageAgent) blockedIds.add("ai-agent");
        if (!canManageTransaction) blockedIds.add("client-payment-logs");
        if (!canManageRefund) blockedIds.add("refund-requests");

        break;

      default:
        break;
    }

    const deepFilter = (list: SidebarItem[]): SidebarItem[] =>
      list
        .filter((item) => !blockedIds.has(item.id))
        .map((item) =>
          item.children
            ? { ...item, children: deepFilter(item.children) }
            : item,
        );

    let pruned = deepFilter(items);
    if (role !== Role.SALES_AGENT)
      pruned = pruned.filter((item) => item.id !== "sponsor");
    return pruned;
  };

  useEffect(() => {
    if (!pendingScrollToBottom || !scrollRef.current) return;

    // small delay so Framer Motion can apply height
    const id = setTimeout(() => {
      if (!scrollRef.current) return;
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 80);

    return () => clearTimeout(id);
  }, [expandedItems, pendingScrollToBottom]);

  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const base = tenantAdminItems;
    const rolePruned = pruneForRole(base, user.role);
    setSideBarItems(rolePruned);
  }, [user]);

  const isActive = (path: string) => {
    if (path === "/dashboard" || path === "/") {
      return location.pathname === "/dashboard" || location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const toggleExpanded = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });

    // Wait a bit so framer-motion can expand/collapse height
    setTimeout(() => {
      const container = scrollRef.current;
      const itemEl = itemRefs.current[itemId];
      if (!container || !itemEl) return;

      const itemRect = itemEl.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const isBelow = itemRect.bottom > containerRect.bottom;
      const isAbove = itemRect.top < containerRect.top;

      if (isBelow) {
        // scroll down just enough so bottom is visible
        container.scrollBy({
          top: itemRect.bottom - containerRect.bottom + 8,
          behavior: "smooth",
        });
      } else if (isAbove) {
        // scroll up just enough so top is visible
        container.scrollBy({
          top: itemRect.top - containerRect.top - 8,
          behavior: "smooth",
        });
      }
    }, 200); // adjust if your animation is slower/faster
  };

  const renderMenuItem = (item: SidebarItem, level: number = 0) => {
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id);
    const isActiveItem = item.path ? isActive(item.path) : false;

    const paddingLeft = collapsed
      ? "justify-center"
      : level > 0
        ? "pl-8 pr-0"
        : "pl-2 pr-2";

    if (hasChildren) {
      return (
        <div
          key={item.id}
          ref={(el) => {
            itemRefs.current[item.id] = el;
          }}
        >
          <button
            onClick={() => toggleExpanded(item.id)}
            className={cn(
              "group flex items-center w-full rounded-md transition-colors mb-1",
              collapsed ? "py-3" : "py-3",
              isActiveItem
                ? "bg-yellow-500 text-gray-800"
                : "text-white hover:bg-yellow-500 hover:text-gray-900",
              item.disabled &&
              "opacity-50 cursor-not-allowed pointer-events-none",
            )}
            aria-expanded={isExpanded}
          >
            <div className={cn("flex items-center flex-1", paddingLeft)}>
              <Icon
                size={18}
                className={cn("flex-shrink-0", collapsed ? "mx-auto" : "mr-3")}
              />
              {!collapsed && (
                <div className="flex items-center justify-between w-full">
                  <span className="text-sm font-medium truncate">
                    {item.label}
                  </span>
                  {item.badge && (
                    <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-blue-600 rounded-full">
                      {item.badge}
                    </span>
                  )}
                  {isExpanded ? (
                    <ChevronUp
                      size={16}
                      className="ml-3 text-yellow-300 hover:text-white"
                    />
                  ) : (
                    <ChevronDown size={16} className="ml-3 text-yellow-300" />
                  )}
                </div>
              )}
            </div>
          </button>

          {/* AnimatePresence + motion for smooth open/close */}
          <AnimatePresence initial={false}>
            {isExpanded && (
              <motion.div
                key={`${item.id}-submenu`}
                className="ml-0 mt-1 space-y-1 overflow-hidden"
                initial="collapsed"
                animate="open"
                exit="collapsed"
                variants={{
                  open: {
                    height: "auto",
                    opacity: 1,
                    transform: "translateY(0px)",
                  },
                  collapsed: {
                    height: 0,
                    opacity: 0,
                    transform: "translateY(-6px)",
                  },
                }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                  duration: 0.18,
                }}
              >
                {item.children!.map((child) =>
                  renderMenuItem(child, level + 1),
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    return (
      <NavLink
        key={item.id}
        to={item.path!}
        className={({ isActive }) =>
          cn(
            "group flex items-center w-full rounded-md transition-colors mb-1",
            collapsed ? "py-3" : "py-3",
            isActive
              ? "bg-yellow-500 text-gray-800"
              : "text-white hover:bg-yellow-500 hover:text-gray-900",
            item.disabled &&
            "opacity-50 cursor-not-allowed pointer-events-none",
          )
        }
        onClick={isMobile ? onClose : undefined}
      >
        <div className={cn("flex items-center flex-1", paddingLeft)}>
          {/* Icon: honors active state and hover via group */}
          <Icon
            size={18}
            className={cn(
              "flex-shrink-0",
              collapsed ? "mx-auto" : "mr-3",
              isActiveItem,
            )}
          />

          {!collapsed && (
            <motion.div
              className="flex items-center flex-1"
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -6 }}
              transition={{ duration: 0.18 }}
            >
              <span className="flex-1 text-sm font-medium truncate">
                {item.label}
              </span>
              {item.badge && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-blue-600 rounded-full">
                  {item.badge}
                </span>
              )}
            </motion.div>
          )}
        </div>
      </NavLink>
    );
  };

  return (
    <motion.div
      className={cn("flex flex-col h-full border-r shadow-sm", className)}
      style={{ backgroundColor: "#195f9a", color: "#fff" }}
      animate={{ width: collapsed ? 64 : 256 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 mb-2">
        <div className="flex items-center w-full">
          <AnimatePresence initial={false} mode="wait">
            {collapsed ? (
              <motion.img
                key="favicon"
                src="/img/logo/Favicon_White.png"
                alt="crm-favicon"
                className="w-8 h-8"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.15 }}
              />
            ) : (
              <motion.img
                key="logo"
                src="/img/logo/LOGO_White.png"
                alt="crm-logo"
                className="w-36"
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -6 }}
                transition={{ duration: 0.18 }}
              />
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto" ref={scrollRef}>
        <div className="px-3 space-y-1">
          {sideBarItems.map((item) => renderMenuItem(item))}
        </div>
      </nav>
    </motion.div>
  );
};

export default Sidebar;
